/**
 * Reporting Layout - Sidebar nav + Outlet for reporting sub-pages
 *
 * Route: /admin/reporting (layout)
 */

import { useState } from 'react'
import { Outlet, Link, useLocation } from '@tanstack/react-router'
import { useAuth } from '@/common/hooks/useAuth'
import { Stack } from '@/common/components/stack'
import { Text } from '@/common/components/text'
import { Button } from '@/common/components/button'
import { Icon } from '@/common/components/icon'
import * as styles from './reporting.css'

export const Route = createFileRoute({
  component: ReportingLayout,
})

const navItems = [
  { to: '/admin/reporting' as const, label: 'Platform Metrics' },
  { to: '/admin/reporting/investor-engagement' as const, label: 'Investor Trends' },
  { to: '/admin/reporting/investors' as const, label: 'Investors' },
  { to: '/admin/reporting/agents' as const, label: 'Agents' },
  { to: '/admin/reporting/email' as const, label: 'Email Performance' },
  { to: '/admin/reporting/stale-offer-reminders' as const, label: 'Stale Offer Reminders' },
]

function ReportingLayout() {
  const { roles } = useAuth()
  const isAdmin = roles.includes('admin')
  const location = useLocation()
  const [collapsed, setCollapsed] = useState(false)

  if (!isAdmin) {
    return (
      <Stack gap="24" style={{ padding: '24px', height: '100%' }}>
        <Stack gap="4">
          <Text as="h1" variant="h4" weight="bold">Reporting</Text>
          <Text variant="bodySm" color="muted">Admin reporting dashboard</Text>
        </Stack>
        <Stack gap="16" align="center" justify="center" style={{ flex: 1 }}>
          <Icon name="lock" size={48} style={{ color: '#999' }} />
          <Text as="h2" variant="bodyLg" weight="bold">Access Restricted</Text>
          <Text variant="body" color="muted" style={{ textAlign: 'center', maxWidth: 400 }}>
            Reporting is only available to administrators.
          </Text>
          <Link to="/properties"><Button>Go to Properties</Button></Link>
        </Stack>
      </Stack>
    )
  }

  return (
    <div className={styles.layout}>
      <nav className={styles.sidebar} style={collapsed ? { width: '40px', overflow: 'hidden' } : undefined}>
        {collapsed ? (
          <button
            onClick={() => setCollapsed(false)}
            className={styles.collapseBtn}
            aria-label="Expand sidebar"
            title="Expand sidebar"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M6.22 3.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L9.94 8 6.22 4.28a.75.75 0 0 1 0-1.06z"/></svg>
          </button>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px' }}>
              <span className={styles.sidebarTitle} style={{ padding: 0, marginBottom: 0 }}>Reporting</span>
              <button
                onClick={() => setCollapsed(true)}
                className={styles.collapseBtn}
                aria-label="Collapse sidebar"
                title="Collapse sidebar"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M9.78 12.78a.75.75 0 0 1-1.06 0L4.47 8.53a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 1.06L6.06 8l3.72 3.72a.75.75 0 0 1 0 1.06z"/></svg>
              </button>
            </div>
            {navItems.map((item) => {
              const isActive = item.to === '/admin/reporting'
                ? location.pathname === item.to
                : location.pathname === item.to || location.pathname.startsWith(item.to + '/')
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`${styles.sidebarLink} ${isActive ? styles.sidebarLinkActive : ''}`}
                >
                  {item.label}
                </Link>
              )
            })}
          </>
        )}
      </nav>
      <div className={styles.content}>
        <Outlet />
      </div>
    </div>
  )
}
